import { action, observable, makeObservable } from "mobx";

import InventoryAPI from "../api/InventoryAPI";
import { InventoryFilters } from "../../../models/inventory/InventoryListingFilters";
import {
  APIResponse,
  APIBlobResponse,
  PaginatedAPIResponse,
} from "../../../models/NetworkResponse";
import { InventoryListing } from "../../../models/inventory/InventoryListing";
import { InventorySwagpackVariant } from "../../../models/inventory/InventorySwagpackVariant";

export class InventoryStore {
  customers: any;
  searchCustomerById(id: string) {
    throw new Error("Method not implemented.");
  }
  ids: number[];
  listing: InventoryListing[];
  variant: InventorySwagpackVariant[];
  limit: number;
  total_pages: number;
  total_count: number;
  inventoryAPI: InventoryAPI;
  static customer_id: any;
  inventories: any;
  orderInventoryId: number | null;
  masterInventoryId: number | null;

  constructor() {
    this.ids = [];
    this.listing = [];
    this.variant = [];
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.inventoryAPI = new InventoryAPI();
    this.orderInventoryId = null;
    this.masterInventoryId = null;

    makeObservable(this, {
      ids: observable,
      listing: observable,
      variant: observable,
      searchIds: action,
      getListing: action,
      getSwagpackVariants: action,
      downloadCountReportCsv: action,
      uploadSwagpackQuantityCsv: action,
      getOrderInventoryIdByOrderId: action,
      getMasterInventoryIdByCustomerId: action,
      orderInventoryId: observable,
      masterInventoryId: observable,
    });
  }

  getOrderInventoryIdByOrderId = async (orderId: number) => {
    try {
      const response =
        await this.inventoryAPI.getOrderInventoryIdByOrderId(orderId);
      this.orderInventoryId = response;
    } catch (error: any) {
      throw error;
    }
  };

  getMasterInventoryIdByCustomerId = async (customerId: number) => {
    try {
      const response =
        await this.inventoryAPI.getMasterInventoryIdByCustomerId(customerId);
      this.masterInventoryId = response;
    } catch (error: any) {
      throw error;
    }
  };

  searchIds = async (searchText: string) => {
    try {
      const list = await this.inventoryAPI.getIds(searchText);
      this.ids = list;
    } catch (error: any) {
      throw error;
    }
  };

  getListing = async (filters: InventoryFilters) => {
    try {
      const response = (await this.inventoryAPI.getListing(
        filters,
      )) as PaginatedAPIResponse<InventoryListing>;
      this.limit = response.limit;
      this.total_count = response.total_count;
      this.total_pages = response.total_pages;
      this.listing = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getSwagpackVariants = async (packId: number) => {
    try {
      const response = (await this.inventoryAPI.getSwagpackVariants(
        packId,
      )) as InventorySwagpackVariant[];
      this.variant = response;
    } catch (error: any) {
      throw error;
    }
  };

  downloadCountReportCsv = async (
    inventoryId: number,
  ): Promise<APIBlobResponse> => {
    try {
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        throw new Error("Invalid inventory ID");
      }

      const response =
        await this.inventoryAPI.downloadCountReportCsv(inventoryId);

      if (!response?.data) {
        throw new Error("No data received for count report");
      }

      return response;
    } catch (error: unknown) {
      const message = this.getDownloadErrorMessage(error);
      throw new Error(message);
    }
  };

  uploadSwagpackQuantityCsv = async (
    inventoryId: number,
    file: File,
  ): Promise<APIResponse> => {
    try {
      return await this.inventoryAPI.uploadSwagpackQuantityCsv(inventoryId, file);
    } catch (error: unknown) {
      throw new Error(
        this.getApiErrorMessage(error, "Failed to upload swagpack quantity CSV"),
      );
    }
  };

  private getApiErrorMessage = (
    error: unknown,
    fallback: string,
  ): string => {
    if (
      typeof error === "object" &&
      error !== null &&
      "response" in error
    ) {
      const data = (error as { response?: { data?: { message?: string } } })
        .response?.data;
      if (typeof data?.message === "string" && data.message.trim()) {
        return data.message;
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallback;
  };

  private getDownloadErrorMessage = (error: unknown): string =>
    this.getApiErrorMessage(error, "Failed to download count report CSV");
}
